import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import { isApiError } from '../client/errors';
import { ContentAuthor, ContentBase } from '../content/contracts';
/**
 * Events (spec §8) — and RSVP, which arrived in round 6.
 *
 * The backend corrected itself here, which is worth recording: they had told
 * the back office `rsvpCount` was "a feature, not a field" and declined it.
 * The distinction was real and the conclusion wrong — the answer to "there is
 * nothing behind this number" is to build the thing behind it.
 */
/**
 * A plan an event is reserved for (« réservé aux membres Mensuel »).
 *
 * `name` comes back in the request's locale. `status` matters because the
 * back office may scope to an ARCHIVED plan — members still on an old price —
 * and a plan nobody can buy any more must not be offered as the way in.
 */
export const EventRequiredPlan = z.object({
    id: z.string().uuid(),
    key: z.string(),
    name: z.string(),
    status: z.enum(['ACTIVE', 'ARCHIVED']),
});
export const Event = ContentBase.extend({
    type: z.literal('EVENT'),
    title: z.string(),
    summary: z.string().optional(),
    kind: z.enum(['WEBINAR', 'LIVE', 'GATHERING']),
    /**
     * The Événements chip it sits under (TSP request 4, 25 Sep 2026), and that
     * chip's label in the reader's locale. Both null when uncategorised; the
     * label is also null when the category was archived. Defaulted so an API
     * predating them reads as uncategorised rather than failing the parse.
     */
    categoryId: z.string().uuid().nullable().default(null),
    categoryLabel: z.string().nullable().default(null),
    /**
     * The cohort journey session this event IS, when it is one — the milestone
     * and the event share their date, start time and join link.
     */
    session: z
        .object({ cohortId: z.string().uuid(), milestoneId: z.string().uuid() })
        .nullable()
        .default(null),
    startsAt: z.string().datetime(),
    endsAt: z.string().datetime().nullable(),
    hostId: z.string().uuid().nullable(),
    /**
     * Who is running the session — and the key is `host`, not `author`.
     *
     * This was declared as `author` and the API answers `host`, so the
     * enrichment was dropped at the boundary and the card could only ever know
     * an id. The comment that used to sit here said "the design's event card
     * leads with who is running the session", which was right about the design
     * and wrong about the field.
     *
     * Worth naming what did NOT catch it: `author` was `.optional()`, so a
     * response without it parses clean. Zod guards the shape it is given, and an
     * optional field that never arrives is indistinguishable from an editor who
     * left it blank. `host` is `.nullable()` and required, which is what the
     * contract says.
     */
    host: ContentAuthor.nullable(),
    rsvpCapacity: z.number().int().positive().nullable(),
    /** Counted on read — nothing is stored on the event. */
    rsvpCount: z.number().int().nonnegative(),
    /** Resolved for the caller. */
    myRsvp: z.boolean(),
    featured: z.boolean(),
    /**
     * The link to attend — present only while it is this member's to use: they
     * hold an RSVP and the door is open. The server decides; see `joinDoor`.
     */
    joinUrl: z.string().nullable(),
    /**
     * Whether the event has a link at all, and the door's hours. Defaulted so an
     * API that predates them reads as "no link" rather than failing the parse.
     */
    hasJoinLink: z.boolean().default(false),
    joinOpensAt: z.string().nullable().default(null),
    joinClosesAt: z.string().nullable().default(null),
    /** Set after the event; makes the recording searchable. */
    replayRef: z.string().nullable(),
    /**
     * The plans that open this event. `[]` means no plan scope, and it is never
     * absent. The server enforces it: a member on none of these plans never gets
     * the event from a list, and an RSVP from them answers 404 — see
     * `explainNotFound`. Nothing here decides access from this field.
     */
    requiredPlans: z.array(EventRequiredPlan),
});
/**
 * Whether this event is the member's own cohort session.
 *
 * A cohort event is already closed to its members — nobody books a place in
 * their own programme — so the API hands the join link over without an RSVP,
 * and a card has no places to count. `cohorts` is the audience that says so;
 * `session` is the milestone link, and either is enough.
 */
export function isCohortSession(event) {
    return event.cohorts.length > 0 || event.session !== null;
}
export function joinDoor(event, now = new Date()) {
    if (event.joinUrl)
        return 'open';
    if (!event.hasJoinLink)
        return 'none';
    if (event.joinClosesAt && now.getTime() > new Date(event.joinClosesAt).getTime())
        return 'none';
    return event.myRsvp ? 'waiting' : 'rsvpFirst';
}
/**
 * Milliseconds until the next door opens among these events, or `null`.
 *
 * What a screen sets a timer for: the link is withheld by the server until
 * then, so the list has to be asked again at that moment for a `waiting` card
 * to become an `open` one without the member pulling to refresh.
 */
export function nextJoinOpening(events, now = new Date()) {
    let soonest = null;
    for (const event of events) {
        if (joinDoor(event, now) !== 'waiting' || !event.joinOpensAt)
            continue;
        // Already past `joinOpensAt` and still waiting means the list is stale:
        // ask again shortly rather than at once, so a clock a few seconds ahead of
        // the server's does not spin.
        const wait = Math.max(new Date(event.joinOpensAt).getTime() - now.getTime(), 0) + 2_000;
        if (soonest === null || wait < soonest)
            soonest = wait;
    }
    return soonest;
}
/** How long before the start the door opens — for the sentence that says so. */
export function joinLeadMinutes(event) {
    if (!event.joinOpensAt)
        return null;
    return Math.round((new Date(event.startsAt).getTime() - new Date(event.joinOpensAt).getTime()) / 60_000);
}
const paged = (item) => z.object({
    items: z.array(item),
    total: z.number().int().nonnegative(),
    page: z.number().int().positive(),
    pageSize: z.number().int().positive(),
    locale: z.string(),
});
/**
 * The cast pins the OUTPUT type.
 *
 * `ContentBase.ageBandIds` carries a `.default([])`, so the schema's input type
 * makes it optional and its output type does not. Handed to a generic
 * `z.ZodType<T>`, TypeScript resolves `T` from the input and every caller ends
 * up with `string[] | undefined` on a field the parse guarantees is present.
 * The same cast is in `library.ts` for the same reason.
 */
const EventSchema = Event;
/**
 * One window of events, optionally one chip's worth.
 *
 * `category` filters on the server (`GET /events?category=`), so a chip shows
 * every event filed under it rather than the ones that happened to be on the
 * first page of the unfiltered list.
 */
export function useEvents(http, when, locale, options = {}) {
    const category = options.category ?? null;
    return useQuery({
        queryKey: ['events', when, locale, category ?? 'all'],
        queryFn: () => http.get('/events', {
            query: { when, ...(category ? { category } : {}) },
            schema: paged(EventSchema),
            locale,
        }),
    });
}
/**
 * An Événements filter chip (`GET /events/categories`). Live ones only, in the
 * order an editor set. `eventCount` is filled for staff and null for a member,
 * so a screen decides which chips to draw from its own list, never from it.
 */
export const EventCategory = z.object({
    id: z.string().uuid(),
    slug: z.string(),
    position: z.number(),
    label: z.string(),
    archivedAt: z.string().nullable().default(null),
    eventCount: z.number().int().nonnegative().nullable().default(null),
});
export function useEventCategories(http, locale) {
    return useQuery({
        // Not under `['events']`: an RSVP invalidates that prefix, and the chips do not move with it.
        queryKey: ['event-categories', locale],
        queryFn: () => http
            .get('/events/categories', {
            schema: z.object({ items: z.array(EventCategory) }),
            locale,
        })
            .then((answer) => [...answer.items].filter((c) => !c.archivedAt).sort((a, b) => a.position - b.position)),
        staleTime: 5 * 60_000,
    });
}
/**
 * Hold or release a place.
 *
 * **No `Idempotency-Key`, and that is a conclusion rather than an omission.**
 * Holding a place is set membership, so a repeated request writes nothing — and
 * critically, it is not refused at capacity for the place you already hold.
 * What is genuinely unsafe is two different people racing for the last place,
 * which no idempotency key would help; the server locks the event row for that.
 */
export async function toggleRsvp(http, { eventId, on }, locale) {
    try {
        return await http.post(`/events/${eventId}/rsvp`, { on }, { schema: EventSchema, locale });
    }
    catch (error) {
        if (isApiError(error) && error.code === 'EVENT_NOT_FOUND') {
            throw await explainNotFound(http, eventId, error, locale);
        }
        throw error;
    }
}
export function useToggleRsvp(http, locale) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (input) => toggleRsvp(http, input, locale),
        onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['events'] }),
    });
}
/**
 * The RSVP was refused because the event is reserved for plans this member is
 * not on — as the server states them NOW.
 */
export class EventLocked extends Error {
    requiredPlans;
    constructor(requiredPlans) {
        super('EVENT_LOCKED');
        this.requiredPlans = requiredPlans;
        this.name = 'EventLocked';
    }
}
/**
 * An RSVP 404 means one of two things, and only a fresh read can tell them apart.
 *
 * A member on none of an event's plans gets `EVENT_NOT_FOUND` from the RSVP
 * route, exactly as for a premium event. They only meet the event from a page
 * that loaded while they qualified, so the page is stale — and its copy of
 * `requiredPlans` is stale too. That copy was once used to explain the refusal,
 * and driving it showed why it cannot be: when the SCOPE changed rather than
 * the member's plan, the stale list names the member's own plan back to them —
 * « réservé aux membres Mensuel » to a Monthly member, about an event just
 * moved to Annual.
 *
 * `GET /events/{id}` answers the truth. A locked teaser (200, `locked: true`)
 * carries the plans that open the event now. A 404 means the event itself is
 * gone — unpublished or deleted — and the original refusal stands.
 *
 * Unlocked on the re-read means the scope changed back between the two
 * requests; that is "try again", not "gone".
 */
async function explainNotFound(http, eventId, original, locale) {
    try {
        const current = await http.get(`/events/${eventId}`, { schema: EventSchema, locale });
        return current.locked ? new EventLocked(current.requiredPlans) : new Error('RSVP_RETRY');
    }
    catch {
        return original;
    }
}
export function rsvpRefusal(error) {
    if (!error)
        return null;
    if (error instanceof EventLocked) {
        const plans = error.requiredPlans.filter((plan) => plan.status === 'ACTIVE').map((plan) => plan.name);
        return plans.length > 0 ? { key: 'events.reservedFor', plans } : { key: 'events.gone' };
    }
    if (isApiError(error) && error.code === 'EVENT_AT_CAPACITY')
        return { key: 'events.atCapacity' };
    if (isApiError(error) && error.code === 'EVENT_NOT_FOUND')
        return { key: 'events.gone' };
    return { key: 'events.rsvpFailed' };
}
//# sourceMappingURL=events.js.map